import React, {useState} from 'react';
import {Box, Button, Divider, Drawer, IconButton, Stack, Tab, Tabs, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {map, Observable, of} from 'rxjs';

import {TableFilter, TableFilterType} from '@store/common/tableFilter.model';

import {Close} from '@icons/index';

import {Sizes} from '@constants/sizes.constants';
import AsyncMultiSelectWithSearchComponent
  from '@components/inputs/asyncMultiSelect/AsyncMultiSelectWithSearch.component';
import {FilterBaseEntityEnum, filtersService} from '@store/filters';
import {Domain, OrganisationKind} from '@store/common/organisation.enums';
import {historyService, HistoryType} from '@store/settings/history';
import {ServiceKindTypeEnum} from '@store/settings/serviceKinds';

interface TableFiltersDrawerProps {
  filters: TableFilter[];
  excludeFilters?: TableFilter[];
  handleClose: () => void;
  handleSave: (tableFilters: TableFilter[], excludeFilters: TableFilter[]) => void;
  baseEntity: FilterBaseEntityEnum;
}

const TableFiltersDrawer = (props: TableFiltersDrawerProps) => {
  const {filters, excludeFilters, handleClose, handleSave} = props;

  const {t} = useTranslation();

  const [tabFilter, setTabFilter] = useState<'include' | 'exclude'>('include');
  const [newFilters, setNewFilters] = useState<TableFilter[]>(filters);
  const [newExcludeFilters, setNewExcludeFilters] = useState<TableFilter[]>(excludeFilters || []);

  const updateFilters = (name: string, filterIndex: number) => (value: any) => {
    const filtersToUpdate = [...(newFilters || [])];
    filtersToUpdate[filterIndex] = {...filtersToUpdate[filterIndex], [name]: value};
    setNewFilters(filtersToUpdate);
  };

  const updateExcludeFilters = (name: string, filterIndex: number) => (value: any) => {
    const filtersToUpdate = [...(newExcludeFilters || [])];
    filtersToUpdate[filterIndex] = {...filtersToUpdate[filterIndex], [name]: value};
    setNewExcludeFilters(filtersToUpdate);
  };

  return (
    <Drawer anchor="right" open onClose={handleClose}>
      <Stack flex={1} overflow="hidden">
        <Stack mr="10px">
          <Stack>
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb="12px" mr="-8px">
              <Typography fontSize={18} fontWeight="700">
                {t('global.filters')}
              </Typography>
              <IconButton onClick={handleClose}>
                <Close/>
              </IconButton>
            </Stack>
            <Divider variant="fullWidth" orientation="horizontal"/>
          </Stack>
          <Divider variant="fullWidth" orientation="horizontal"/>
        </Stack>
        <Tabs value={tabFilter} onChange={(_, value) => setTabFilter(value)}>
          <Tab value="include"
               label={t('global.includeFilters') + ` (${(newFilters.reduce((acc, val) => val.values.length > 0 ? acc + 1 : acc, 0))})`}/>
          {excludeFilters && <Tab value="exclude"
                                  label={t('global.excludeFilters') + ` (${(newExcludeFilters.reduce((acc, val) => val.values.length > 0 ? acc + 1 : acc, 0))})`}/>}
        </Tabs>
        <Stack pt="20px" flex={1} spacing={1} overflow="hidden">
          <Stack spacing={3} overflow="auto" className="scrollable" py="20px" pr="10px">
            {(tabFilter === 'include' ? newFilters : newExcludeFilters).map((filter, index) => {
              if (filter.hide) return null;
              return (
                <Stack key={`Table filter ${filter.type} - ${index}`} mr="-5px">
                  <Stack pr="5px">
                    <AsyncMultiSelectWithSearchComponent
                      labelWidth={Sizes.drawerInputLabelWidth}
                      label={t(`global.filterFieldType.${filter.type}`)}
                      placeholder={t('global.selectOptions')}
                      readOnly={filter.disabled}
                      handleChange={tabFilter === 'include' ? updateFilters('values', index) : updateExcludeFilters('values', index)}
                      values={filter.values ?? []}
                      getOptions={(search) => {
                        let searchObservable: Observable<any> = of([]);
                        switch (filter.type) {
                          case TableFilterType.ORGANISATION_TYPOLOGY:
                            return of(OrganisationKind.selectItems()
                              .filter((o) => !search || o.label.toLowerCase().includes(search.toLowerCase()))
                            );
                          case TableFilterType.ORGANISATION_DOMAIN:
                            return of(Domain.selectItems()
                              .filter((o) => !search || o.label.toLowerCase().includes(search.toLowerCase()))
                            );
                          case TableFilterType.TYPE_ACTION:
                            return of(
                              HistoryType.selectItemsCompare().filter(
                                (o) => !search || o.label.toLowerCase().includes(search.toLowerCase())
                              )
                            );
                          case TableFilterType.USER:
                            const authorIds = newFilters
                              .find((of) => of.type === TableFilterType.USER)
                              ?.values?.map((v) => v.value);
                            return (searchObservable = historyService.searchHistories(search, authorIds).pipe(
                              map((options) =>
                                options.data.map((o) => ({
                                  label: `${o.authorLastName} ${o.authorFirstName}` || '-',
                                  value: o.authorId,
                                }))
                              )
                            ));
                          case TableFilterType.SERVICE_KIND_TYPE:
                          case TableFilterType.EXCLUDE_SERVICE_KIND_TYPE:
                            return of(ServiceKindTypeEnum.selectItems().filter(
                              (o) => !search || o.label.toLowerCase().includes(search.toLowerCase())
                            ));
                          default:
                            searchObservable = filtersService.getFilters(filter.type, props.baseEntity, tabFilter === 'include' ? newFilters : newExcludeFilters, search);
                        }
                        return searchObservable?.pipe(
                          map((options) => options.map((o) => ({label: o.name || '-', value: o.id})))
                        );

                      }}/>
                  </Stack>
                </Stack>
              );
            })}
          </Stack>
        </Stack>
        <Box alignSelf="flex-end" justifySelf="flex-end" my="20px" mr="10px">
          <Button
            variant="contained"
            color="primary"
            disabled={JSON.stringify(filters) === JSON.stringify(newFilters) && JSON.stringify(excludeFilters) === JSON.stringify(newExcludeFilters)}
            onClick={() => handleSave(newFilters, newExcludeFilters)}>
            {t('global.modify')}
          </Button>
        </Box>
      </Stack>
    </Drawer>
  );
};

export default TableFiltersDrawer;
